import React, { useState, useEffect } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    MenuItem,
} from '@material-ui/core'
import { Provider, ProviderBillingMode } from '../../../models/Outreach/types'

interface ProviderDialogProps {
    open: boolean
    onClose: () => void
    onSave: (provider: {
        name: string
        credit_cost: number
        billing_mode: ProviderBillingMode
    }) => void
    provider?: Provider | null
}

const BILLING_MODES: ProviderBillingMode[] = [
    ProviderBillingMode.PER_RESULT,
    ProviderBillingMode.PER_SEARCH,
]

export const ProviderDialog: React.FC<ProviderDialogProps> = ({
    open,
    onClose,
    onSave,
    provider,
}) => {
    const [formData, setFormData] = useState({
        name: '',
        credit_cost: '',
        billing_mode: '',
    })

    const [errors, setErrors] = useState<{ [key: string]: string }>({})

    useEffect(() => {
        if (provider) {
            setFormData({
                name: provider.name || '',
                credit_cost: provider.credit_cost?.toString() || '',
                billing_mode: provider.billing_mode || '',
            })
        } else {
            setFormData({
                name: '',
                credit_cost: '',
                billing_mode: '',
            })
        }
        setErrors({})
    }, [provider, open])

    const handleInputChange = (field: string, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }))
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: '' }))
        }
    }

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {}

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required'
        }

        if (!formData.credit_cost.trim()) {
            newErrors.credit_cost = 'Credit cost is required'
        } else {
            const cost = parseFloat(formData.credit_cost)
            if (isNaN(cost) || cost < 0) {
                newErrors.credit_cost =
                    'Credit cost must be a valid number >= 0'
            }
        }

        if (!formData.billing_mode.trim()) {
            newErrors.billing_mode = 'Billing mode is required'
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSave = () => {
        if (validateForm()) {
            onSave({
                name: formData.name.trim(),
                credit_cost: parseFloat(formData.credit_cost),
                billing_mode: formData.billing_mode as ProviderBillingMode,
            })
        }
    }

    const handleClose = () => {
        setFormData({
            name: '',
            credit_cost: '',
            billing_mode: '',
        })
        setErrors({})
        onClose()
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {provider ? 'Edit Provider' : 'Create Provider'}
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} style={{ marginTop: 8 }}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Name"
                            value={formData.name}
                            onChange={(e) =>
                                handleInputChange('name', e.target.value)
                            }
                            error={!!errors.name}
                            helperText={errors.name}
                            required
                            variant="outlined"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Credit Cost"
                            type="number"
                            value={formData.credit_cost}
                            onChange={(e) =>
                                handleInputChange('credit_cost', e.target.value)
                            }
                            error={!!errors.credit_cost}
                            helperText={errors.credit_cost}
                            required
                            variant="outlined"
                            inputProps={{ min: 0, step: 0.01 }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            select
                            label="Billing Mode"
                            value={formData.billing_mode}
                            onChange={(e) =>
                                handleInputChange(
                                    'billing_mode',
                                    e.target.value,
                                )
                            }
                            error={!!errors.billing_mode}
                            helperText={errors.billing_mode}
                            required
                            variant="outlined"
                        >
                            {BILLING_MODES.map((mode) => (
                                <MenuItem key={mode} value={mode}>
                                    {mode}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="primary">
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    color="primary"
                    variant="contained"
                >
                    {provider ? 'Update' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
